import React, { useState } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import PushNotificationToggle from '../common/PushNotificationToggle.tsx';
import { AppIconName, SidebarNavIcon } from '../../icons/appIcons.tsx';
import LogoMark from '../common/LogoMark.tsx';

interface AlumniSidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  onLogout: () => void;
  alumniName?: string;
}

type MenuItem = { id: string; label: string; icon: AppIconName };

export default function AlumniSidebar({ currentView, onNavigate, onLogout, alumniName }: AlumniSidebarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const isActive = (view: string) => currentView === view;

  const handleNavigate = (view: string) => {
    onNavigate(view);
    setMobileMenuOpen(false);
  };

  const menuSections: { title: string; items: MenuItem[] }[] = [
    {
      title: 'Alumni Portal',
      items: [
        { id: 'dashboard', label: 'Home', icon: 'home' },
        { id: 'profile', label: 'My Profile', icon: 'profile' },
        { id: 'invoices', label: 'Invoices & Payments', icon: 'payment' },
        { id: 'horses', label: 'Horses', icon: 'horse' }
      ]
    },
    {
      title: 'Communication',
      items: [
        { id: 'events', label: 'Events & Calendar', icon: 'events' },
        { id: 'notifications', label: 'Notifications', icon: 'bell' },
        { id: 'documents', label: 'Documents', icon: 'documents' },
        { id: 'directory', label: 'Ranch Directory', icon: 'directory' }
      ]
    }
  ];

  const logoBlock = (
    <>
      {!logoFailed ? (
        <img
          src="/images/ggaa-logo.png"
          alt="GGAA Logo"
          className="h-8 w-8 lg:h-10 lg:w-10 rounded-lg bg-white p-1 shrink-0"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <LogoMark className="h-8 w-8 lg:h-10 lg:w-10" />
      )}
    </>
  );

  return (
    <>
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-ggaa-green to-ggaa-dark border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3">
            {logoBlock}
            <div>
              <h1 className="text-sm font-bold text-white">God's Green Acre</h1>
              <p className="text-xs text-white/80">{alumniName || 'Alumni Portal'}</p>
            </div>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40 mt-16" onClick={() => setMobileMenuOpen(false)} />
      )}

      <div
        className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-gray-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:h-screen
        mt-16 lg:mt-0
      `}
      >
        <div className="hidden lg:block bg-gradient-to-r from-ggaa-green to-ggaa-dark p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {logoBlock}
            <div className="flex-1 min-w-0">
              <h1 className="text-sm font-bold text-white truncate">God's Green Acre</h1>
              <p className="text-xs text-white/80 truncate">{alumniName || 'Alumni Portal'}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          {menuSections.map((section, idx) => (
            <div key={idx} className="mb-6">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map(item => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigate(item.id)}
                    className={`group w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.id) ? 'bg-ggaa-green text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <SidebarNavIcon name={item.icon} active={isActive(item.id)} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200 space-y-3">
          <PushNotificationToggle compact={true} />
          <button
            onClick={onLogout}
            className="w-full text-center px-3 py-2 text-sm text-ggaa-green hover:text-ggaa-dark font-medium transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
}
